import { pollColorPalette } from "../utils/pollColorPalette";
import { formatRankLabel } from "../utils/rankingResultsUtils";

interface HistogramV2Props {
  optionId: string | undefined;
  counts: number[];
  rankColors: string[];
  averageRank: number;
  showAverageMarker?: boolean;
  userRank: number | undefined;
  userRankBucketIndex: number | null;
  averagePosition: number;
  displayRankCount: number;
  maxRank: number;
  responseCount: number;
  maxDensity: number;
}

export const RankingHistogramV2 = ({
  optionId,
  counts,
  rankColors,
  averageRank,
  averagePosition,
  displayRankCount,
  maxRank,
  responseCount,
  maxDensity,
  userRank,
  userRankBucketIndex,
  showAverageMarker = true,
}: HistogramV2Props) => {
  const unrankedIndex = maxRank;
  const unrankedCount = counts[unrankedIndex] ?? 0;
  const unrankedColor = pollColorPalette.unrankedOverflow;
  const overflowSpan = Math.max(0, maxRank - displayRankCount);

  const topBuckets = counts.slice(0, displayRankCount).map((count, rankIndex) => {
    const density = count;
    return {
      key: `${optionId}-histogram-v2-top-${rankIndex}`,
      label: formatRankLabel(rankIndex + 1),
      count,
      color: rankColors[rankIndex] ?? unrankedColor,
      density,
      isUserRank: userRankBucketIndex === rankIndex,
      percentage: responseCount === 0 ? 0 : (count / responseCount) * 100,
    };
  });

  const lowRankBuckets = counts.slice(displayRankCount, maxRank).map((count, idx) => {
    const rankIndex = displayRankCount + idx;
    return {
      key: `${optionId}-histogram-v2-low-${rankIndex}`,
      label: formatRankLabel(rankIndex + 1),
      count,
      color: rankColors[rankIndex] ?? unrankedColor,
      density: count,
      isUserRank: userRankBucketIndex === rankIndex,
      percentage: responseCount === 0 ? 0 : (count / responseCount) * 100,
    };
  });

  const unrankedDensity = overflowSpan > 0 ? unrankedCount / overflowSpan : unrankedCount;
  const computedMaxDensity = Math.max(
    ...topBuckets.map((bucket) => bucket.density),
    ...lowRankBuckets.map((bucket) => bucket.density),
    unrankedDensity,
    0
  );
  const normalizedMaxDensity = maxDensity > 0 ? maxDensity : computedMaxDensity;

  const normalizedUnrankedHeight =
    normalizedMaxDensity > 0 ? (unrankedDensity / normalizedMaxDensity) * 100 : 0;
  const unrankedBarHeight = Math.max(normalizedUnrankedHeight, unrankedCount > 0 ? 6 : 0);
  const unrankedBarOffset = Math.min(100, unrankedBarHeight);
  const markers: {
    key: string;
    label: string;
    position: number;
    color: string;
    labelBg: string;
    labelText: string;
  }[] = [];

  if (showAverageMarker && averageRank) {
    markers.push({
      key: "average",
      label: `Avg ${averageRank.toFixed(1)}`,
      position: averagePosition,
      color: pollColorPalette.averageIndicator,
      labelBg: "#ffffff",
      labelText: pollColorPalette.averageIndicator,
    });
  }

  const clampPosition = (position: number) => Math.min(100, Math.max(0, position));
  const clampedMarkerPositions = markers.map((marker) => clampPosition(marker.position));
  const markerOverlapThreshold = 4;
  const markerBaseTop = 6;
  const markerStaggerOffset = 22;
  const markerTopOffsets = clampedMarkerPositions.map((position, index) => {
    const overlaps = clampedMarkerPositions
      .slice(0, index)
      .some((prev) => Math.abs(prev - position) < markerOverlapThreshold);
    return overlaps ? markerBaseTop + markerStaggerOffset : markerBaseTop;
  });

  return (
    <div className="relative w-full space-y-3">
      <div className="relative flex items-end gap-1 h-28 px-2 pt-4 pb-4 overflow-hidden">
        {topBuckets.map((bucket) => {
          const normalizedHeight = normalizedMaxDensity > 0 ? (bucket.density / normalizedMaxDensity) * 100 : 0;
          const barHeight = Math.max(normalizedHeight, bucket.count > 0 ? 6 : 0);
          const showUserLabelAbove = bucket.isUserRank && !!userRank && barHeight < 20;
          const showUserLabelInside = bucket.isUserRank && !!userRank && !showUserLabelAbove;
          return (
            <div
              key={bucket.key}
              className="flex flex-col items-center gap-1 min-w-[40px] h-full"
              style={{ flexGrow: 1, flexBasis: 0 }}
            >
              <div className="relative w-full h-full flex items-end">
                <div
                  className="relative w-full overflow-hidden rounded-sm"
                  style={{ height: `${Math.min(100, barHeight)}%` }}
                  title={`${bucket.label}: ${bucket.count} response${bucket.count === 1 ? "" : "s"} (${bucket.percentage.toFixed(1)}%)`}
                >
                  <div className="absolute inset-0" style={{ backgroundColor: bucket.color }} />
                  {bucket.isUserRank && (
                    <div className="absolute inset-0 border-[2px] border-black rounded-sm pointer-events-none" />
                  )}
                  {showUserLabelInside && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-black">
                      <span className="px-1.5 py-0.5 rounded bg-white/85">{userRank}</span>
                    </div>
                  )}
                </div>
                {showUserLabelAbove ? (
                  <div className="absolute -top-11 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[10px] font-semibold rounded border bg-background shadow-sm">
                    {userRank}
                  </div>
                ) : null}
              </div>
              <span className="text-[10px] text-center text-muted-foreground leading-tight">
                {bucket.label}
              </span>
            </div>
          );
        })}

        {overflowSpan > 0 ? (
          <div
            className="flex flex-col items-center gap-1 min-w-[80px] h-full"
            style={{ flexGrow: overflowSpan, flexBasis: 0 }}
          >
            <div className="relative w-full flex-1">
              <div
                className="absolute inset-x-1 bottom-0 w-full overflow-hidden rounded-sm"
                style={{ height: `${Math.min(100, unrankedBarHeight)}%` }}
                title={`Unranked: ${unrankedCount} response${unrankedCount === 1 ? "" : "s"} (${responseCount === 0 ? 0 : ((unrankedCount / responseCount) * 100).toFixed(1)}%)`}
              >
                <div className="absolute inset-0" style={{ backgroundColor: unrankedColor }} />
                {userRankBucketIndex === unrankedIndex && (
                  <div className="absolute inset-0 border-[2px] border-black rounded-sm pointer-events-none" />
                )}
              </div>

              <div
                className="absolute inset-x-1 flex items-end gap-1"
                style={{
                  bottom: `${unrankedBarOffset}%`,
                  height: `calc(100% - ${unrankedBarOffset}%)`,
                }}
              >
                {lowRankBuckets.map((bucket) => {
                  const normalizedHeight =
                    normalizedMaxDensity > 0 ? (bucket.density / normalizedMaxDensity) * 100 : 0;
                  const barHeight = Math.max(normalizedHeight, bucket.count > 0 ? 6 : 0);
                  const showUserLabelAbove = bucket.isUserRank && !!userRank && barHeight < 20;
                  const showUserLabelInside = bucket.isUserRank && !!userRank && !showUserLabelAbove;
                  return (
                    <div
                      key={bucket.key}
                      className="relative flex-1 min-w-[22px] h-full flex items-end"
                      title={`${bucket.label}: ${bucket.count} response${bucket.count === 1 ? "" : "s"} (${bucket.percentage.toFixed(1)}%)`}
                    >
                      <div
                        className="relative w-full overflow-hidden rounded-sm"
                        style={{ height: `${Math.min(100, barHeight)}%` }}
                      >
                        <div className="absolute inset-0" style={{ backgroundColor: bucket.color }} />
                        {bucket.isUserRank && (
                          <div className="absolute inset-0 border-[2px] border-black rounded-sm pointer-events-none" />
                        )}
                        {showUserLabelInside && (
                          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-black">
                            <span className="px-1.5 py-0.5 rounded bg-white/85">{userRank}</span>
                          </div>
                        )}
                      </div>
                      {showUserLabelAbove ? (
                        <div className="absolute -top-11 left-1/2 -translate-x-1/2 px-2 py-0.5 text-[10px] font-semibold rounded border bg-background shadow-sm">
                          {userRank}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex w-full items-start gap-1">
              {lowRankBuckets.map((bucket) => (
                <span
                  key={`${bucket.key}-label`}
                  className="flex-1 min-w-[22px] text-[10px] text-center text-muted-foreground leading-tight"
                >
                  {bucket.label}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {markers.length > 0 ? (
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            {markers.map((marker) => (
              <div
                key={`${optionId}-${marker.key}-line`}
                className="absolute top-2 bottom-8 w-[3px]"
                style={{
                  left: `${clampPosition(marker.position)}%`,
                  backgroundColor: marker.color,
                }}
              />
            ))}
          </div>
        ) : null}

        {markers.map((marker, index) => (
          <div
            key={`${optionId}-${marker.key}-label`}
            className="pointer-events-none absolute text-[10px] font-semibold px-2 py-0.5 rounded shadow-sm border"
            style={{
              left: `${clampedMarkerPositions[index]}%`,
              transform: "translateX(-50%)",
              backgroundColor: marker.labelBg,
              color: marker.labelText,
              borderColor: marker.color,
              top: `${markerTopOffsets[index]}px`,
            }}
          >
            {marker.label}
          </div>
        ))}
      </div>
    </div>
  );
};
